import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import usePageTitle from 'hooks/usePageTitle';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { Joyride } from 'react-joyride';
import { getJoyrideScrollOffset } from 'utils/joyrideConfig';
import { user } from '@solimat/solimat-web-endpoint';
import useGetProvinces from 'services/company/useGetProvinces';
import useGetCentersByProvince from 'services/company/useGetCentersByProvince';
import useDecode from 'hooks/useDecode';
import IconArrowGray from '../../../img/icons/svg/botones/flecha-big-gris.svg';
import SolimatLogoIcon from '../../../img/NewDesign/logo-pequeño.svg';
import PageMenu from '../PageMenu/PageMenu';
import LoaderMin from 'components/blocks/Loader/LoaderMin';
import Icon from 'components/blocks/Icon/Icon';
import CentersMap from './CentersMap';
import { useCentersTour } from 'hooks/useCentersTour';
import { sortCentersByPriority } from 'utils/centerSorting';
import './CentersNew.scss';

const JOYRIDE_PROPS = {
  continuous: true,
  scrollToFirstStep: true,
  locale: { back: 'Anterior', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', nextWithProgress: 'Siguiente ({current} de {total})', open: 'Iniciar tour de ayuda', skip: 'Omitir tour' },
  options: { primaryColor: '#0F1F3D', zIndex: 10000, overlayColor: 'rgba(15,47,73,0.45)', showProgress: true, buttons: ['back', 'skip', 'close', 'primary'] } as any,
  styles: { beaconInner: { backgroundColor: '#F97316' }, beaconOuter: { borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.2)' }, tooltip: { borderRadius: 8, fontSize: 14 }, tooltipTitle: { fontSize: 15, fontWeight: 700, textAlign: 'left' as const }, tooltipContent: { textAlign: 'left' as const } },
};

const BASE_URL = import.meta.env.VITE_REACT_APP_USUARIOS_PATH || '';
const FUNCTIONS_KEY = import.meta.env.VITE_REACT_APP_USUARIOS_KEY || '';
const userService = new user.AuthService(BASE_URL, FUNCTIONS_KEY);

const CentersNew: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Red de Centros');
  const tokenResponse = useDecode('token');
  const [filterValue, setFilterValue] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [centers, setCenters] = useState([]);
  const [addressSearch, setAddressSearch] = useState('');
  const [loadingUser, setLoadingUser] = useState(true);

  const { getProvinces, loadingGetProvinces } = useGetProvinces();
  const { getCentersByProvince, loadingGetCenterByProvince } = useGetCentersByProvince();

  const { run, tourKey, steps, handleCallback, startTour } = useCentersTour(centers.length > 0);

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('solimat:startCentersTour', handler);
    return () => window.removeEventListener('solimat:startCentersTour', handler);
  }, [startTour]);

  const handleChange = async (args: any) => {
    setFilterValue(args.itemData.Descripcion);
    setSelectedProvince(args.value);
    const result = await getCentersByProvince(`${args.value}`);
    setCenters(result);
    setAddressSearch('');
    localStorage.setItem('selectedProvince', args.value);
    localStorage.setItem('selectedProvinceName', args.itemData.Descripcion);
    localStorage.setItem('selectedCenters', JSON.stringify(result));
  };

  useEffect(() => {
    const init = async () => {
      const result = await getProvinces().catch(() => []);
      const filtered = result.filter(
        (x: { Id: string; Descripcion: string; Codigo: string }) => x.Codigo !== '99'
      );
      setProvinces(filtered);

      const savedProvince = localStorage.getItem('selectedProvince');
      const savedProvinceName = localStorage.getItem('selectedProvinceName');
      const savedCenters = localStorage.getItem('selectedCenters');

      if (savedProvince && savedProvinceName && savedCenters) {
        setSelectedProvince(savedProvince);
        setFilterValue(savedProvinceName);
        setCenters(JSON.parse(savedCenters));
      }

      if (tokenResponse?.extension_id) {
        try {
          const res: any = await userService.GetCompleteUser(tokenResponse.extension_id);
          const dirs: any[] = res?.direcciones ?? [];
          const mainDir = dirs.find((d: any) => d.principal) ?? dirs[0];

          if (!savedProvince && mainDir?.codigoProvincia) {
            const matchProv: any = filtered.find(
              (p: any) => p.Codigo === mainDir.codigoProvincia
            );
            if (matchProv) {
              setSelectedProvince(matchProv.Codigo);
              setFilterValue(matchProv.Descripcion);
              const centersResult = await getCentersByProvince(mainDir.codigoProvincia);
              setCenters(centersResult);
            }
          }
        } catch {
          // silently ignore
        }
      }
      setLoadingUser(false);
    };
    init();
  }, []);

  const fields = { text: 'Descripcion', value: 'Codigo' };

  const handleRedirectCenter = (id: string) => {
    navigate(`/red-de-centros/centro/${id}`);
  };

  const q = addressSearch.trim().toLowerCase();

  const visibleCenters: any[] = sortCentersByPriority(
    q
      ? centers.filter((c: any) => {
          const addr = `${c.TipoViaDescripcion ?? ''} ${c.NombreCalle ?? ''} ${c.NumeroCalle ?? ''} ${c.LocalidadDescripcion ?? ''}`.toLowerCase();
          return addr.includes(q) || c.Descripcion?.toLowerCase().includes(q);
        })
      : centers
  );

  return (
    <>
      <Joyride
        key={tourKey}
        steps={steps}
        run={run}
        onEvent={handleCallback}
        {...JOYRIDE_PROPS}
        scrollOffset={getJoyrideScrollOffset()}
      />
      <PageMenu title="Red de centros" subtitle='Encuentra el centro Solimat más cercano a ti.' breadcrumbs={['Red de centros']}>
        <div className="btns-header-container">
          <Link to="/">
            <img src={IconArrowGray} alt="icon arrow right" />
          </Link>
        </div>
      </PageMenu>

      <section className="section">
        <div className="container">
          <div className="centros-filter reveal is-in">
            <div className="centros-filter__copy">
              <span className="centros-filter__icon" aria-hidden>
                <Icon name="pin" size={22} />
              </span>
              <div>
                <h2 className="centros-filter__title">¿Dónde quieres ser atendido?</h2>
                <p className="centros-filter__hint">
                  Selecciona tu provincia para ver los centros Solimat más cercanos a tu ubicación.
                </p>
              </div>
            </div>
            <div className="centros-filter__field">
              <label htmlFor="centersElements" className="centros-filter__label">
                Provincia
              </label>
              {loadingGetProvinces && <LoaderMin />}
              <div style={{ display: loadingGetProvinces ? 'none' : 'block' }}>
                <DropDownListComponent
                  id="centersElements"
                  dataSource={provinces}
                  popupHeight="200px"
                  placeholder="Selecciona provincia"
                  change={handleChange}
                  fields={fields}
                  value={selectedProvince}
                />
              </div>
            </div>
          </div>

          {loadingUser ? (
            <div className="centros-empty">
              <LoaderMin />
              <p style={{ marginTop: 16 }}>Cargando centros y mapa…</p>
            </div>
          ) : filterValue !== '' ? (
            loadingGetCenterByProvince ? (
              <LoaderMin />
            ) : (
              <div className="centros-group">
                <div className="centros-group__head">
                  <h2 className="centros-group__title">{filterValue}</h2>
                  <span className="centros-group__count">
                    {q && visibleCenters.length !== centers.length
                      ? `${visibleCenters.length} de ${centers.length} centros`
                      : `${centers?.length ?? 0} centros`}
                  </span>
                </div>

                <CentersMap allCenters={centers} visibleCenters={visibleCenters} />

                {centers?.length > 0 && (
                  <div className="centros-search">
                    <Icon name="search" size={16} />
                    <input
                      className="centros-search__input"
                      type="text"
                      placeholder="Buscar por dirección o nombre del centro…"
                      value={addressSearch}
                      onChange={(e) => setAddressSearch(e.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {addressSearch && (
                      <button
                        className="centros-search__clear"
                        onClick={() => setAddressSearch('')}
                        aria-label="Limpiar búsqueda"
                      >
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>
                )}

                {centers?.length > 0 ? (
                  visibleCenters.length > 0 ? (
                    <div className="centers">
                      {visibleCenters.map((center: any, i: number) => {
                        const tipoCentroId = Number(center.TipoCentroId);
                        const isHospital = tipoCentroId === 4 || tipoCentroId === 5;
                        const isSolimat = center.Descripcion?.toLowerCase().includes('solimat');
                        const centerClasses = ['center', 'reveal', 'is-in'];
                        if (isHospital) centerClasses.push('center--hospital');
                        if (isSolimat) centerClasses.push('center--solimat');
                        if (!isHospital && !isSolimat) centerClasses.push('center--centro');
                        const patternId = `p-${center.Id || i}`;
                        const addr = `${center.TipoViaDescripcion ?? ''} ${center.NombreCalle ?? ''} ${center.NumeroCalle ?? ''}, ${center.LocalidadDescripcion ?? ''}`.trim();
                        return (
                          <article
                            key={center.Id || i}
                            className={centerClasses.join(' ')}
                            role="button"
                            tabIndex={0}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRedirectCenter(center.Id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRedirectCenter(center.Id);
                              }
                            }}
                          >
                            <div className="center__hero" aria-hidden>
                              <svg className="center__pattern" viewBox="0 0 200 120" preserveAspectRatio="none">
                                <defs>
                                  <pattern id={patternId} width="22" height="22" patternUnits="userSpaceOnUse">
                                    <circle cx="11" cy="11" r="1.4" fill="currentColor" opacity="0.35" />
                                  </pattern>
                                </defs>
                                <rect width="200" height="120" fill={`url(#${patternId})`} />
                              </svg>
                              <div className="center__glyph">
                                {isSolimat
                                  ? <img src={SolimatLogoIcon} alt="Solimat" className="center__glyph-logo" />
                                  : isHospital
                                    ? <Icon name="hospital" size={36} />
                                    : <Icon name="shield" size={36} />}
                              </div>
                              {center.LocalidadDescripcion && (
                                <div className="center__city">
                                  <Icon name="pin" size={14} /> {center.LocalidadDescripcion}
                                </div>
                              )}
                            </div>
                            <div className="center__body">
                              <div className="center__name">{center.Descripcion}</div>
                              <div className="center__addr">{addr}</div>
                              {center.Telefono && (
                                <a href={`tel:${center.Telefono}`} className="center__phone">
                                  <Icon name="phone" size={16} /> {center.Telefono}
                                </a>
                              )}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="centros-empty">
                      <div className="centros-empty__icon">
                        <Icon name="search" size={32} />
                      </div>
                      <h3>Sin resultados</h3>
                      <p>No se encontraron centros para «{addressSearch}».</p>
                    </div>
                  )
                ) : (
                  <div className="centros-empty">
                    <div className="centros-empty__icon">
                      <Icon name="hospital" size={32} />
                    </div>
                    <h3>Sin centros disponibles</h3>
                    <p>No hay centros disponibles para esta provincia.</p>
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="centros-empty centros-empty--prompt">
              <div className="centros-empty__icon">
                <Icon name="pin" size={32} />
              </div>
              <h3>Selecciona una provincia</h3>
              <p>
                Elige una provincia del desplegable para ver los centros disponibles en tu zona.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CentersNew;
